import {
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui";
import { retrieveMatterQueryOptions } from "@/lib/data/react-query/matters/matter";
import { retrieveObjectMatterMembershipsQueryOptions } from "@/lib/data/react-query/matters/matter-related-object";
import type { SharedItemMembership } from "@/lib/data/schemas/matters/SharedItemMembershipSchema";
import type { RelatedMatterObjectType } from "@/lib/data/requests/matters/matter-related-object";
import { cn } from "@/lib/utils/cn";
import { getMatterPartyConnections } from "@/lib/utils/connections/matter-party-connections";
import { useQueries, useQuery } from "@tanstack/react-query";
import { BriefcaseBusiness, Link2 } from "lucide-react";
import { useMemo, useState } from "react";

type SharedItemObjectType = Exclude<RelatedMatterObjectType, "incident">;

const objectLabels: Record<SharedItemObjectType, string> = {
  insurance: "insurance",
  "incident-report": "incident report",
  vehicle: "vehicle",
};

interface MembershipPopoverProps {
  canUnlink?: boolean;
  currentMatterId: string;
  description?: string;
  heading?: string;
  isSuccess: boolean;
  itemLabel: string;
  matters: SharedItemMembership["matters"];
  onUnlink?: () => void;
  triggerClassName?: string;
}

function MembershipPopover({
  canUnlink,
  currentMatterId,
  description,
  heading = "Shared across related matters",
  isSuccess,
  itemLabel,
  matters: unsortedMatters,
  onUnlink,
  triggerClassName,
}: MembershipPopoverProps) {
  const [open, setOpen] = useState(false);
  const matters = useMemo(
    () =>
      [...unsortedMatters].sort((left, right) => {
        if (left.id === currentMatterId) return -1;
        if (right.id === currentMatterId) return 1;
        return (left.title ?? "").localeCompare(right.title ?? "");
      }),
    [currentMatterId, unsortedMatters],
  );
  const relatedMatterCount = matters.filter(
    (matter) => matter.id !== currentMatterId,
  ).length;
  const countLabel = `${relatedMatterCount} related ${relatedMatterCount === 1 ? "matter" : "matters"}`;
  const triggerLabel = `Linked to ${countLabel}`;

  if (!isSuccess || relatedMatterCount === 0) {
    return null;
  }

  return (
    <Popover modal open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          className={cn(
            "h-8 gap-1 rounded-full px-2 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300",
            triggerClassName,
          )}
          aria-label={triggerLabel}
          aria-expanded={open}
          title={triggerLabel}
          onClick={(event) => {
            event.stopPropagation();
          }}
          onKeyDown={(event) => {
            event.stopPropagation();
          }}
        >
          <Link2 className="size-4" />
          <span className="text-sm font-semibold">{relatedMatterCount}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        data-modal-popover="true"
        align="start"
        sideOffset={12}
        className="w-[min(500px,calc(100vw-2rem))] rounded-2xl p-0 shadow-xl"
        onClick={(event) => {
          event.stopPropagation();
        }}
        onKeyDown={(event) => {
          event.stopPropagation();
        }}
      >
        <div className="flex gap-4 px-8 pt-7 pb-5">
          <Link2 className="mt-0.5 size-5 shrink-0" />
          <div>
            <h3 className="text-base font-semibold">{heading}</h3>
            <p className="text-muted-foreground mt-1 text-sm leading-5">
              {description ??
                `Changes to this ${itemLabel} appear everywhere it is linked.`}
            </p>
          </div>
        </div>

        <div className="max-h-72 overflow-y-auto px-8">
          {matters.map((matter) => (
            <div
              key={matter.id}
              className="flex min-h-14 items-center gap-3 border-b py-3 last:border-b-0"
            >
              <BriefcaseBusiness className="size-5 shrink-0" />
              <span className="min-w-0 flex-1 truncate text-sm font-medium">
                {matter.title || "Untitled matter"}
              </span>
              {matter.id === currentMatterId ? (
                <span className="shrink-0 text-sm font-medium text-emerald-700">
                  Current
                </span>
              ) : null}
            </div>
          ))}
        </div>

        <div className="mt-3 flex min-h-16 items-center justify-between gap-4 border-t px-8 py-4">
          <span className="text-muted-foreground text-sm font-medium">
            {countLabel}
          </span>
          {canUnlink && onUnlink ? (
            <button
              type="button"
              className="text-destructive hover:text-destructive/80 shrink-0 text-sm font-semibold"
              onClick={() => {
                setOpen(false);
                onUnlink();
              }}
            >
              Unlink from this matter
            </button>
          ) : null}
        </div>
      </PopoverContent>
    </Popover>
  );
}

interface LinkedMatterMembershipPopoverProps {
  canUnlink?: boolean;
  currentMatterId: string;
  enabled: boolean;
  objectId: string;
  objectType: SharedItemObjectType;
  onUnlink?: () => void;
}

export function LinkedMatterMembershipPopover({
  canUnlink,
  currentMatterId,
  enabled,
  objectId,
  objectType,
  onUnlink,
}: LinkedMatterMembershipPopoverProps) {
  const memberships = useQuery(
    retrieveObjectMatterMembershipsQueryOptions({
      objectType,
      objectId,
      currentMatterId,
      enabled,
    }),
  );

  return (
    <MembershipPopover
      canUnlink={canUnlink}
      currentMatterId={currentMatterId}
      isSuccess={memberships.isSuccess}
      itemLabel={objectLabels[objectType]}
      matters={memberships.data?.data.matters ?? []}
      onUnlink={onUnlink}
    />
  );
}

export function LinkedMatterContactConnectionMembershipPopover({
  contactId,
  currentMatterId,
  relatedMatterIds,
}: {
  contactId: string;
  currentMatterId: string;
  relatedMatterIds: string[];
}) {
  const matterIds = useMemo(
    () => [...new Set([currentMatterId, ...relatedMatterIds])],
    [currentMatterId, relatedMatterIds],
  );
  const matterQueries = useQueries({
    queries: matterIds.map((matterId) => retrieveMatterQueryOptions(matterId)),
  });
  const matters = useMemo(
    () =>
      matterQueries.flatMap((query) => {
        const matter = query.data?.data;
        if (
          !matter ||
          !getMatterPartyConnections(matter).some(
            (connection) => connection.targetObjectId === contactId,
          )
        ) {
          return [];
        }
        return [{ id: matter.id, title: matter.title }];
      }),
    [contactId, matterQueries],
  );

  return (
    <MembershipPopover
      currentMatterId={currentMatterId}
      description="This contact appears in these related matters."
      heading="Contact on related matters"
      isSuccess={matterQueries.every((query) => query.isSuccess)}
      itemLabel="party"
      matters={matters}
      triggerClassName="border-0 bg-transparent text-blue-400 shadow-none hover:bg-transparent hover:text-blue-300 dark:text-blue-400 dark:hover:text-blue-300"
    />
  );
}
